// شاشة حالة الحساب (قيد المراجعة / مرفوض / معلّق)
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import BrandLockup from "@/v2/components/BrandLockup";
import "@/v2/v2.css";
import { Clock, RefreshCw, LogOut, ShieldAlert, PauseCircle, Building2, UserCog } from "lucide-react";

const VIEWS = {
  pending: {
    icon: Clock, tone: "#e3c799", bg: "rgba(187,150,97,0.14)", title: "حسابك قيد المراجعة",
    body: "تم استلام طلبك بنجاح، وسيتم تفعيل حسابك بعد موافقة إدارة المنصة. ستصلك رسالة داخل المنصة عند الاعتماد.",
  },
  rejected: {
    icon: ShieldAlert, tone: "#F7A08A", bg: "rgba(180,83,9,0.16)", title: "لم يُعتمد طلب الانضمام",
    body: "يمكنك تعديل بياناتك وإعادة الإرسال، أو التواصل مع إدارة المنصة لمزيد من التفاصيل.",
  },
  suspended: {
    icon: PauseCircle, tone: "#F7A08A", bg: "rgba(180,83,9,0.16)", title: "الحساب معلّق مؤقتًا",
    body: "تم تعليق حسابك مؤقتًا من إدارة المنصة. تواصل مع الإدارة لإعادة التفعيل.",
  },
};

export default function Pending() {
  const { user, refresh, logout } = useAuth();
  const navigate = useNavigate();
  const [checking, setChecking] = useState(false);
  const v = VIEWS[user?.approval_status] || VIEWS.pending;
  const I = v.icon;

  const check = async () => {
    setChecking(true);
    await refresh();
    setChecking(false);
    toast.info("تم تحديث حالة الحساب");
  };

  return (
    <div className="v2-root min-h-screen flex flex-col items-center justify-center p-5 v2-grid v2-pattern relative overflow-hidden" dir="rtl">
      <div className="absolute top-[-15%] right-[-10%] w-[500px] h-[500px] rounded-full bg-[#91cab8]/10 blur-3xl v2-float pointer-events-none" />
      <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
        className="w-full max-w-lg relative z-10">
        <div className="flex justify-center mb-8"><BrandLockup tone="dark" /></div>
        <div className="v2-glass v2-glass-gold p-8 sm:p-10 text-center" data-testid="pending-card">
          <div className="w-20 h-20 mx-auto rounded-full flex items-center justify-center mb-6 v2-pulse"
            style={{ color: v.tone, background: v.bg }}>
            <I size={38} />
          </div>
          <h1 className="text-2xl sm:text-3xl font-extrabold mb-3" data-testid="pending-title">{v.title}</h1>
          <p className="text-[#bcc8cf] leading-relaxed mb-6">{v.body}</p>

          {user?.reject_reason && user?.approval_status === "rejected" && (
            <div className="v2-inset p-4 text-sm text-[#F7A08A] mb-6" data-testid="pending-reason">السبب: {user.reject_reason}</div>
          )}

          <div className="v2-inset p-4 space-y-2 text-sm text-start mb-7">
            <div className="flex items-center gap-2"><UserCog size={15} className="text-[#bcc8cf]" /> <span className="text-[#bcc8cf]">الاسم:</span> <b>{user?.name}</b></div>
            {user?.department && <div className="flex items-center gap-2"><Building2 size={15} className="text-[#bcc8cf]" /> <span className="text-[#bcc8cf]">الإدارة:</span> <b>{user.department}</b></div>}
            {user?.employee_type_label && <div className="flex items-center gap-2"><Clock size={15} className="text-[#bcc8cf]" /> <span className="text-[#bcc8cf]">المسار المطلوب:</span> <b>{user.employee_type_label}</b></div>}
          </div>

          <div className="flex flex-wrap gap-3 justify-center">
            <button onClick={check} disabled={checking} data-testid="pending-refresh" className="v2-btn">
              <RefreshCw size={16} className={checking ? "animate-spin" : ""} /> تحديث الحالة
            </button>
            {user?.approval_status === "rejected" && (
              <button onClick={() => navigate("/onboarding")} data-testid="pending-redo" className="v2-btn-ghost">تعديل بياناتي</button>
            )}
            <button onClick={() => { logout(); navigate("/login"); }} data-testid="pending-logout" className="v2-btn-ghost">
              <LogOut size={16} /> تسجيل الخروج
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
